import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminService, SuscripcionItem } from '../../services/superAdminService';
import { useSuscripciones } from '../../hooks/useSuscripciones';

const SuscripcionesPage: React.FC = () => {
  // Estados para filtros y búsqueda
  const [searchTerm, setSearchTerm] = useState('');
  const [filterEmpresa, setFilterEmpresa] = useState<number | ''>('');
  const [filterPlan, setFilterPlan] = useState<number | ''>('');
  const [filterEstado, setFilterEstado] = useState<'todos' | 'Activa' | 'Vencida' | 'Cancelada'>('todos');
  const [sortBy] = useState<'fechaInicio' | 'fechaFin' | 'empresa' | 'plan'>('fechaInicio');

  const {
    suscripciones,
    isLoading,
    error,
    refetch,
    isFetching,
    createSuscripcion,
    updateSuscripcion,
    deleteSuscripcion,
    isCreating,
    isUpdating,
  } = useSuscripciones();

  const { data: empresas } = useQuery({
    queryKey: ['superEmpresasResumen'],
    queryFn: superAdminService.getEmpresasResumen,
  });

  const { data: planes } = useQuery({
    queryKey: ['superPlanes'],
    queryFn: superAdminService.getPlanes,
  });

  const createMutation = {
    mutate: (payload: { empresaId: number; planId: number; periodo?: 'Mensual' | 'Anual'; renuevaAutomatico: boolean; featureOverridesJson?: string }) =>
      createSuscripcion(payload, {
        onSuccess: () => {
          setShowCreateForm(false);
          setFormData({ empresaId: '', planId: '', periodo: 'Mensual', renuevaAutomatico: true, featureOverridesJson: '' });
        },
      }),
    isPending: isCreating,
  };

  const updateMutation = {
    mutate: ({ id, payload }: { id: number; payload: { estado: string; planId?: number; periodo?: 'Mensual' | 'Anual'; renuevaAutomatico: boolean; fechaInicio?: string; fechaFin?: string; featureOverridesJson?: string } }) =>
      updateSuscripcion({ id, payload }, {
        onSuccess: () => {
          setShowEditForm(false);
          setEditingSuscripcion(null);
        },
      }),
    isPending: isUpdating,
  };

  const deleteMutation = {
    mutate: (id: number) => deleteSuscripcion(id),
  };

  // Estados del formulario
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingSuscripcion, setEditingSuscripcion] = useState<SuscripcionItem | null>(null);
  const [editFechaInicio, setEditFechaInicio] = useState<string>('');
  const [editFechaFin, setEditFechaFin] = useState<string>('');
  const [formData, setFormData] = useState({ 
    empresaId: '', 
    planId: '', 
    periodo: 'Mensual' as 'Mensual' | 'Anual',
    renuevaAutomatico: true, 
    featureOverridesJson: '' 
  });
  const [formErrors, setFormErrors] = useState<{ empresaId?: string; planId?: string }>({});

  // Validar formulario
  const validateForm = (): boolean => {
    const errors: { empresaId?: string; planId?: string } = {};
    
    if (!formData.empresaId) {
      errors.empresaId = 'La empresa es requerida';
    }

    if (!formData.planId) {
      errors.planId = 'El plan es requerido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Filtrar y ordenar suscripciones
  const suscripcionesFiltradas = useMemo(() => {
    if (!suscripciones) return [];
    
    let filtered = suscripciones.filter(suscripcion => {
      const empresa = empresas?.find(e => e.id === suscripcion.empresaId);
      const plan = planes?.find(p => p.id === suscripcion.planId);
      
      const matchesSearch = empresa?.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           plan?.nombre.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesEmpresa = !filterEmpresa || suscripcion.empresaId === filterEmpresa;
      const matchesPlan = !filterPlan || suscripcion.planId === filterPlan;
      const matchesEstado = filterEstado === 'todos' || suscripcion.estado === filterEstado;
      
      return matchesSearch && matchesEmpresa && matchesPlan && matchesEstado;
    });

    // Ordenar
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'fechaInicio':
          return new Date(b.fechaInicio).getTime() - new Date(a.fechaInicio).getTime();
        case 'fechaFin':
          return new Date(b.fechaFin || '').getTime() - new Date(a.fechaFin || '').getTime();
        case 'empresa':
          const empresaA = empresas?.find(e => e.id === a.empresaId)?.nombre || '';
          const empresaB = empresas?.find(e => e.id === b.empresaId)?.nombre || '';
          return empresaA.localeCompare(empresaB);
        case 'plan':
          const planA = planes?.find(p => p.id === a.planId)?.nombre || '';
          const planB = planes?.find(p => p.id === b.planId)?.nombre || '';
          return planA.localeCompare(planB);
        default:
          return 0;
      }
    });

    return filtered;
  }, [suscripciones, empresas, planes, searchTerm, filterEmpresa, filterPlan, filterEstado, sortBy]);

  const handleCreateSuscripcion = () => {
    if (!validateForm()) return;
    
    createMutation.mutate({
      empresaId: Number(formData.empresaId),
      planId: Number(formData.planId),
      periodo: formData.periodo,
      renuevaAutomatico: formData.renuevaAutomatico,
      featureOverridesJson: formData.featureOverridesJson.trim() || undefined
    });
  };

  const handleEditSuscripcion = () => {
    if (!validateForm() || !editingSuscripcion) return;
    // Construir payload con fechas
    const payload: { estado: string; periodo: 'Mensual' | 'Anual'; renuevaAutomatico: boolean; featureOverridesJson?: string; fechaInicio?: string; fechaFin?: string } = {
      estado: editingSuscripcion.estado,
      periodo: formData.periodo,
      renuevaAutomatico: formData.renuevaAutomatico,
      featureOverridesJson: formData.featureOverridesJson.trim() || undefined,
    };
    if (editFechaInicio) {
      // Enviar como ISO en UTC a medianoche
      payload.fechaInicio = new Date(`${editFechaInicio}T00:00:00Z`).toISOString();
    }
    if (editFechaFin) {
      payload.fechaFin = new Date(`${editFechaFin}T00:00:00Z`).toISOString();
    } else {
      payload.fechaFin = undefined;
    }

    updateMutation.mutate({
      id: editingSuscripcion.id,
      payload
    });
  };

  const openEditForm = (suscripcion: SuscripcionItem) => {
    setEditingSuscripcion(suscripcion);
    setFormData({
      empresaId: suscripcion.empresaId.toString(),
      planId: suscripcion.planId.toString(),
      periodo: (suscripcion.periodo as 'Mensual' | 'Anual') ?? 'Mensual',
      renuevaAutomatico: suscripcion.renuevaAutomatico,
      featureOverridesJson: suscripcion.featureOverridesJson || ''
    });
    const toInputDate = (iso: string) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');
    setEditFechaInicio(toInputDate(suscripcion.fechaInicio));
    setEditFechaFin(suscripcion.fechaFin ? toInputDate(suscripcion.fechaFin) : '');
    setShowEditForm(true);
  };

  const handleDeleteSuscripcion = (suscripcion: SuscripcionItem) => {
    const empresa = empresas?.find(e => e.id === suscripcion.empresaId)?.nombre || '';
    if (window.confirm(`¿Estás seguro de que quieres eliminar la suscripción de "${empresa}"?`)) {
      deleteMutation.mutate(suscripcion.id);
    }
  };

  const getEstadoColor = (estado: string) => {
    switch (estado) {
      case 'Activa':
        return 'bg-green-100 text-green-800';
      case 'Vencida':
        return 'bg-red-100 text-red-800';
      case 'Cancelada':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <div className="text-gray-500 mt-2">Cargando suscripciones...</div>
        </div>
      </div>
    );
  }

  if (error || !suscripciones) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 mb-4">Error al cargar las suscripciones</div>
        <button 
          onClick={() => refetch()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Suscripciones</h2>
          <p className="text-gray-600 mt-1">Gestiona las suscripciones de las empresas</p>
        </div>
        <div className="flex items-center gap-3">
          <button 
            onClick={() => refetch()} 
            disabled={isFetching}
            className="px-4 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors"
          >
            {isFetching ? 'Actualizando...' : 'Actualizar'}
          </button>
          <button
            onClick={() => setShowCreateForm(true)}
            className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Nueva Suscripción
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Buscar</label>
            <input
              type="text"
              placeholder="Empresa o plan..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
            <select
              value={filterEmpresa}
              onChange={(e) => setFilterEmpresa(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Todas las empresas</option>
              {empresas?.map(empresa => (
                <option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
            <select
              value={filterPlan}
              onChange={(e) => setFilterPlan(e.target.value ? Number(e.target.value) : '')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Todos los planes</option>
              {planes?.map(plan => (
                <option key={plan.id} value={plan.id}>{plan.nombre}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Estado</label>
            <select
              value={filterEstado}
              onChange={(e) => setFilterEstado(e.target.value as any)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="todos">Todos</option>
              <option value="Activa">Activa</option>
              <option value="Vencida">Vencida</option>
              <option value="Cancelada">Cancelada</option>
            </select>
          </div>
          <div className="flex items-end">
            <div className="text-sm text-gray-600">
              {suscripcionesFiltradas.length} de {suscripciones.length} suscripciones
            </div>
          </div>
        </div>
      </div>

      {/* Formulario de creación */}
      {showCreateForm && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Crear nueva suscripción</h3>
            <button
              onClick={() => setShowCreateForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
              <select
                value={formData.empresaId}
                onChange={(e) => setFormData({ ...formData, empresaId: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.empresaId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar empresa...</option>
                {empresas?.map(empresa => (
                  <option key={empresa.id} value={empresa.id}>{empresa.nombre}</option>
                ))}
              </select>
              {formErrors.empresaId && (
                <p className="text-red-600 text-sm mt-1">{formErrors.empresaId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={formData.planId}
                onChange={(e) => setFormData({ ...formData, planId: e.target.value })}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  formErrors.planId ? 'border-red-300' : 'border-gray-300'
                }`}
              >
                <option value="">Seleccionar plan...</option>
                {planes?.map(plan => (
                  <option key={plan.id} value={plan.id}>{plan.nombre}</option>
                ))}
              </select>
              {formErrors.planId && (
                <p className="text-red-600 text-sm mt-1">{formErrors.planId}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Periodo</label>
              <select
                value={formData.periodo}
                onChange={(e) => setFormData({ ...formData, periodo: e.target.value as 'Mensual' | 'Anual' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="Mensual">Mensual</option>
                <option value="Anual">Anual</option>
              </select>
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Feature Overrides (JSON)</label>
            <textarea
              placeholder='{"maxUsers": 15, "features": ["reports", "qr", "analytics"]}'
              value={formData.featureOverridesJson}
              onChange={(e) => setFormData({ ...formData, featureOverridesJson: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
            />
          </div>
          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="renuevaAutomatico"
              checked={formData.renuevaAutomatico}
              onChange={(e) => setFormData({ ...formData, renuevaAutomatico: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="renuevaAutomatico" className="ml-2 text-sm text-gray-700">
              Renovación automática
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowCreateForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleCreateSuscripcion}
              disabled={createMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {createMutation.isPending ? 'Creando...' : 'Crear Suscripción'}
            </button>
          </div>
        </div>
      )}

      {/* Formulario de edición */}
      {showEditForm && editingSuscripcion && (
        <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Editar suscripción</h3>
            <button
              onClick={() => setShowEditForm(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
              <input
                type="text"
                value={empresas?.find(e => e.id === editingSuscripcion.empresaId)?.nombre || ''}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <input
                type="text"
                value={planes?.find(p => p.id === editingSuscripcion.planId)?.nombre || ''}
                disabled
                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Periodo</label>
              <select
                value={formData.periodo}
                onChange={(e) => setFormData({ ...formData, periodo: e.target.value as 'Mensual' | 'Anual' })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="Mensual">Mensual</option>
                <option value="Anual">Anual</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
              <input
                type="date"
                value={editFechaInicio}
                onChange={(e) => setEditFechaInicio(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
              <input
                type="date"
                value={editFechaFin}
                onChange={(e) => setEditFechaFin(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Feature Overrides (JSON)</label>
            <textarea
              placeholder='{"maxUsers": 15, "features": ["reports", "qr", "analytics"]}'
              value={formData.featureOverridesJson}
              onChange={(e) => setFormData({ ...formData, featureOverridesJson: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
            />
          </div>
          <div className="flex items-center mt-4">
            <input
              type="checkbox"
              id="renuevaAutomatico-edit"
              checked={formData.renuevaAutomatico}
              onChange={(e) => setFormData({ ...formData, renuevaAutomatico: e.target.checked })}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="renuevaAutomatico-edit" className="ml-2 text-sm text-gray-700">
              Renovación automática
            </label>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <button
              onClick={() => setShowEditForm(false)}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={handleEditSuscripcion}
              disabled={updateMutation.isPending}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>
      )}

      {/* Lista de suscripciones */}
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Empresa
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Inicio
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Fecha Fin
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {suscripcionesFiltradas.map(suscripcion => {
                const empresa = empresas?.find(e => e.id === suscripcion.empresaId);
                const plan = planes?.find(p => p.id === suscripcion.planId);
                
                return (
                  <tr key={suscripcion.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{empresa?.nombre}</div>
                      <div className="text-sm text-gray-500">{empresa?.slug}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{plan?.nombre}</div>
                      <div className="text-sm text-gray-500">{plan?.codigo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEstadoColor(suscripcion.estado)}`}>
                        {suscripcion.estado}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(suscripcion.fechaInicio).toLocaleDateString('es-ES')}
                    </td>
                                         <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                       {suscripcion.fechaFin ? new Date(suscripcion.fechaFin).toLocaleDateString('es-ES') : '-'}
                     </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => openEditForm(suscripcion)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => handleDeleteSuscripcion(suscripcion)}
                          className="text-red-600 hover:text-red-900"
                        >
                          Eliminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {suscripcionesFiltradas.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">No se encontraron suscripciones</div>
        </div>
      )}
    </div>
  );
};

export default SuscripcionesPage;
